<template>
  <BaseContainer
    v-if="mainSection && (mainList || otherList)"
    :inner-class="baseContainerInnerClass()"
    :tag="baseContainerTag()"
  >
    <div>
      <h3>{{ ls(title()) }}</h3>
      <p
        v-if="description()"
        class="hina-top-section-description"
      >{{ ls(description()) }}</p>
    </div>

    <template v-if="$slots.default">
      <slot />
    </template>

    <template v-else>
      <div v-if="mainList">
        <template v-if="isTile()">
          <CurationList noheader :def="mainList" />
        </template>
        <template v-else-if="isBullet()">
          <CurationList v-slot="slotProps" noheader :def="mainList">
            <ViewHinaSearchKeywordCard :items="slotProps.items" :def="slotProps.def" />
          </CurationList>
        </template>
        <template v-else-if="isMap()">
          <div class="search-by-map-inner">
            <ClientOnly>
              <CurationList noheader :def="mainList" />
            </ClientOnly>
          </div>
        </template>
        <template v-else-if="isCard()">
          <ul class="hina-linked-database-list">
            <li
              v-for="part in databaseCurationsList(mainList)"
              :key="part.def.cpid"
              class="jps-top-others-item"
            >
              <BaseLink
                :to="linkParamToLink(part.internalLink, part.link)"
                class="hina-linked-database-link"
                :show-icon="false"
              >
                <div
                  class="hina-linked-database-body"
                >
                  <BaseIcon
                    name="icon/db"
                    size="size-3"
                    class="icon"
                    alt=""
                  />

                  <span
                    class="hina-linked-database-heading"
                  >{{ ls(part.title) }}</span>
                </div>
                <div class="hina-linked-database-image">
                  <UiImageSelection
                    v-if="part.image?.imgType"
                    :image="part.image"
                    image-usage="content"
                  />
                </div>
              </BaseLink>
            </li>
          </ul>
        </template>
        <template v-else>
          <CurationList noheader :def="mainList" />
        </template>
      </div>
    </template>

    <div class="hina-more-block" v-if="otherListLink">
      <UiButtonLinkIcon
        :to="linkUrl()"
        :show-icon="!isInnerLink()"
        :icon="isInnerLink() ? 'mdi/chevron-right' : undefined"
      >
        <span class="hina-more-block-text">
          {{ ls(linkTitle()) }}
        </span>
      </UiButtonLinkIcon>
    </div>
  </BaseContainer>
</template>

<script lang="ts" setup>
const { ls } = useI18n()
const { toListElement } = useListElement()
const slots = useSlots()

interface Props {
  curationPart?: CurationPartDefinition
}
const props = withDefaults(defineProps<Props>(), {
})

// データ取得
const mainSection = computed<CurationPartDefinition | null>(() => {
  if (!props.curationPart) {
    return null
  } else if (props.curationPart.type === 'jps-curation-section') {
    return props.curationPart
  } else {
    return null
  }
})
const mainList = computed<CurationPartDefinition | null>(() => {
  if (slots.default) {
    return null
  } else if (!mainSection.value?.parts?.[0]) {
    return null
  } else if (mainSection.value.parts[0].type === 'jps-curation-list') {
    return mainSection.value.parts[0]
  } else {
    return null
  }
})
const otherList = computed<CurationPartDefinition | null>(() => {
  const index = slots.default ? 0 : 1
  if (!mainSection.value?.parts?.[index]) {
    return null
  } else if (mainSection.value.parts[index].type === 'jps-curation-list') {
    return mainSection.value.parts[index]
  } else {
    return null
  }
})
const otherListDescription = computed<PartListExternalLink | null>(() => {
  return otherList.value?.parts?.find((part) => {
    return part.type === 'jps-curation-list-external-link' && (!part.url || part.url === '')
  })
})
const otherListLink = computed<PartListExternalLink | null>(() => {
  return otherList.value?.parts?.find((part) => {
    return part.type === 'jps-curation-list-external-link' && part.url && part.url !== ''
  })
})

// BaseContainer, Section
const baseContainerInnerClass = (): string => {
  return 'hina-top-section'
}
const baseContainerTag = (): string => {
  return 'section'
}

// タイトル・概要
const title = (): LangString => {
  return mainSection.value?.title
}
const description = (): LangString => {
  return otherListDescription.value?.title
}

// 表示部分
const isTile = (): boolean => {
  return mainList.value?.viewType === 'tile'
}
const isBullet = (): boolean => {
  return mainList.value?.viewType === 'bullet'
}
const isMap = (): boolean => {
  return mainList.value?.viewType === 'map'
}
const isCard = (): boolean => {
  return mainList.value?.viewType === 'card'
}
const toListElements = (parts: CurationPartDefinition[]): ListElement[] => {
  return parts.map((p) => toListElement(p))
}
const databaseCurationsList = (part: CurationPartDefinition) => {
  return toListElements(part.parts)
}

// リンク
const linkTitle = (): LangString => {
  return otherListLink.value?.title
}
const linkUrl = (): string => {
  return otherListLink.value?.url
}
const isInnerLink = (): boolean => {
  return otherListLink.value?.url.startsWith('/')
}
</script>

<style lang="scss" scoped>
@use '@/assets/styles/scss/vue' as *;

/**
 * NOTE:
 * .hina-top-section-** は共通スタイルのため、
 * pages/hina/index.vue に記述しています。
 */

.search-by-map-inner {
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;

  :deep(.jps-curation-list-vt-map) {
    width: 100%;
    max-width: initial;
    aspect-ratio: 166 / 75;  // NOTE: Figma で設計された UI に準拠したアスペクト比

     @media screen and (max-width: 440px) {
      aspect-ratio: 342 / 383;  // NOTE: Figma で設計された UI に準拠したアスペクト比
    }

    .curation-search-map {
      &,
      .map-result,
      .jps-map {
        width: 100%;
        height: 100%;
      }
    }
  }
}

.hina-linked-database-list {
  display: grid;
  grid-template-columns: repeat(1, 1fr);
  gap: 1.2rem 0.8rem;

  @include is-tablet {
    grid-template-columns: repeat(2, 1fr);
  }

  @include is-desktop {
    grid-template-columns: repeat(3, 1fr);
  }
}

.hina-linked-database-link {
  display: flex;
  align-items: center;
  border-radius: 1rem;
  border: 1px solid $hina-separator-dark;
  overflow: hidden;

  &:hover {
    .hina-linked-database-body {
      .icon {
        color: $hina-link;
      }
    }
    .hina-linked-database-heading {
      color: $hina-link;
      text-decoration: underline;
    }
  }

  .hina-linked-database-body {
    flex-grow: 1;
    padding: 1rem;
    display: flex;
    align-items: center;
    gap: 0.5rem;

    .icon {
      flex-shrink: 0;
      color: $hina-text-sub;
    }
  }

  .hina-linked-database-heading {
    color: $hina-text-default;
    font-weight: 700;
    font-size: 1rem;
    display: -webkit-box;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 3;
    overflow: hidden;
  }

  .hina-linked-database-image {
    height: 7.5rem;
    width: 7.5rem;
    flex-shrink: 0;

    :deep(img) {
      width: 100%;
      height: 100%;
      aspect-ratio: 1 / 1;
      object-fit: cover !important;
    }
  }
}

:deep(.jps-curation-list) {
  margin: {
    left: 0;
    right: 0;
  }
  padding: {
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
  }
}
</style>
